import { type FC, useSyncExternalStore } from "react";
import { Check, Monitor01, Moon01, Sun } from "@untitledui/icons";
import { Button as AriaButton, Menu as AriaMenu, MenuItem as AriaMenuItem, MenuTrigger as AriaMenuTrigger, Popover as AriaPopover } from "react-aria-components";
import { type FontPreference, PANEL_FONTS, personalFontAllowed, useFont } from "../lib/appearance";
import { cx } from "../utils/cx";

/**
 * Light / dark / system theme. Untitled UI's dark tokens live under `.dark-mode` (theme.css), so the
 * resolved theme is a class on <html>. The choice is stored per browser; the boot script in each
 * app's public/theme-boot.js (same code as THEME_BOOT_SCRIPT; a file so the CSP needs no
 * 'unsafe-inline') applies it before first paint, so there is no flash.
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "mfa-aff:theme";

const listeners = new Set<() => void>();

const readPreference = (): ThemePreference => {
    try {
        const value = window.localStorage.getItem(THEME_STORAGE_KEY);
        return value === "light" || value === "dark" || value === "system" ? value : "system";
    } catch {
        return "system";
    }
};

const systemPrefersDark = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;

export const resolveTheme = (preference: ThemePreference): ResolvedTheme =>
    preference === "system" ? (systemPrefersDark() ? "dark" : "light") : preference;

const applyResolved = (resolved: ResolvedTheme) => {
    const root = document.documentElement;
    root.classList.toggle("dark-mode", resolved === "dark");
    root.style.colorScheme = resolved;
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute("content", resolved === "dark" ? "#0C0E12" : "#FFFFFF");
};

let current: ThemePreference = typeof window === "undefined" ? "system" : readPreference();

const emit = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
    window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener?.("change", () => {
        if (current === "system") {
            applyResolved(resolveTheme("system"));
            emit();
        }
    });
    window.addEventListener("storage", (event) => {
        if (event.key !== THEME_STORAGE_KEY) return;
        current = readPreference();
        applyResolved(resolveTheme(current));
        emit();
    });
}

export const setThemePreference = (preference: ThemePreference) => {
    current = preference;
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
        /* private mode / blocked storage: keep the choice for this tab only */
    }
    applyResolved(resolveTheme(preference));
    emit();
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/** `{ preference, resolved, setPreference }` — re-renders on change, system change and other tabs. */
export const useTheme = () => {
    const preference = useSyncExternalStore(
        subscribe,
        () => current,
        () => "system" as ThemePreference,
    );
    const resolved = useSyncExternalStore(
        subscribe,
        () => resolveTheme(current),
        () => "light" as ResolvedTheme,
    );
    return { preference, resolved, setPreference: setThemePreference };
};

/**
 * Inline (blocking) script for index.html <head>: applies the stored theme before first paint.
 * Keep it in sync with THEME_STORAGE_KEY. Wrapped in try/catch for blocked storage.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var p=localStorage.getItem("${THEME_STORAGE_KEY}");}catch(e){}if(p!=="light"&&p!=="dark")p="system";var d=p==="dark"||(p==="system"&&window.matchMedia&&matchMedia("(prefers-color-scheme: dark)").matches);var r=document.documentElement;if(d)r.classList.add("dark-mode");r.style.colorScheme=d?"dark":"light";})();`;

export const THEME_OPTIONS: { id: ThemePreference; label: string; icon: FC<{ className?: string }> }[] = [
    { id: "light", label: "روشن", icon: Sun },
    { id: "dark", label: "تیره", icon: Moon01 },
    { id: "system", label: "مطابق سیستم", icon: Monitor01 },
];

/** How each font option is drawn in the menu (its own face, so the choice is visible). */
const FONT_FACE: Record<string, string> = { iranyekan: '"IRANYekanX"', iransans: '"IRANSansX"', pinar: '"Pinar"', abar: '"Abar"', lahzeh: '"Lahzeh"' };

/** Header icon button: theme (روشن / تیره / مطابق سیستم) and this person's panel font. */
export const ThemeToggle = ({ className }: { className?: string }) => {
    const { preference, resolved, setPreference } = useTheme();
    const font = useFont();
    const showFont = personalFontAllowed();
    const Icon = resolved === "dark" ? Moon01 : Sun;
    const systemLabel = PANEL_FONTS.find((f) => f.id === font.systemFont)?.label ?? "";
    const fontItems: { id: FontPreference; label: string }[] = [{ id: "system", label: `پیش‌فرض (${systemLabel})` }, ...PANEL_FONTS];

    return (
        <AriaMenuTrigger>
            <AriaButton
                aria-label={showFont ? "تم و فونت نمایش" : "تم نمایش"}
                className={cx(
                    "relative flex size-10 cursor-pointer items-center justify-center rounded-lg text-fg-secondary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:scale-95",
                    className,
                )}
            >
                <Icon className="size-5" aria-hidden="true" />
            </AriaButton>
            <AriaPopover
                placement="bottom end"
                offset={6}
                className={({ isEntering, isExiting }) =>
                    cx(
                        "w-56 origin-(--trigger-anchor-point) overflow-auto rounded-xl bg-primary p-1.5 shadow-lg ring-1 ring-secondary_alt outline-hidden",
                        isEntering && "duration-150 ease-out animate-in fade-in zoom-in-95",
                        isExiting && "duration-100 ease-in animate-out fade-out zoom-out-95",
                    )
                }
            >
                <AriaMenu
                    aria-label="تم نمایش"
                    selectionMode="single"
                    selectedKeys={[preference]}
                    onAction={(key) => setPreference(key as ThemePreference)}
                    className="outline-hidden"
                >
                    {THEME_OPTIONS.map(({ id, label, icon: OptionIcon }) => (
                        <AriaMenuItem
                            key={id}
                            id={id}
                            textValue={label}
                            className="group flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-secondary outline-hidden focus:bg-primary_hover selected:text-primary"
                        >
                            <OptionIcon className="size-4 text-fg-quaternary group-selected:text-fg-brand-primary" aria-hidden="true" />
                            <span className="flex-1">{label}</span>
                            <Check className="invisible size-4 text-fg-brand-primary group-selected:visible" aria-hidden="true" />
                        </AriaMenuItem>
                    ))}
                </AriaMenu>
                {showFont && (
                    <>
                        <div className="mx-1 my-1.5 border-t border-secondary" role="presentation" />
                        <p className="px-2.5 pt-1 pb-1.5 text-xs font-semibold text-quaternary">فونت</p>
                        <AriaMenu
                            aria-label="فونت نمایش"
                            selectionMode="single"
                            selectedKeys={[font.preference]}
                            onAction={(key) => font.setPreference(key as FontPreference)}
                            className="outline-hidden"
                        >
                            {fontItems.map(({ id, label }) => (
                                <AriaMenuItem
                                    key={id}
                                    id={id}
                                    textValue={label}
                                    className="group flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-secondary outline-hidden focus:bg-primary_hover selected:text-primary"
                                >
                                    <span className="flex-1" style={id === "system" ? undefined : { fontFamily: FONT_FACE[id] }}>
                                        {label}
                                    </span>
                                    <Check className="invisible size-4 text-fg-brand-primary group-selected:visible" aria-hidden="true" />
                                </AriaMenuItem>
                            ))}
                        </AriaMenu>
                    </>
                )}
            </AriaPopover>
        </AriaMenuTrigger>
    );
};

/** Inline segmented control (for the account menu / settings). */
export const ThemeSegmented = ({ className }: { className?: string }) => {
    const { preference, setPreference } = useTheme();
    return (
        <div role="radiogroup" aria-label="تم نمایش" className={cx("grid grid-cols-3 gap-1 rounded-lg bg-secondary p-1 ring-1 ring-secondary ring-inset", className)}>
            {THEME_OPTIONS.map(({ id, label, icon: OptionIcon }) => {
                const selected = preference === id;
                return (
                    <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={label}
                        title={label}
                        onClick={() => setPreference(id)}
                        className={cx(
                            "flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md text-xs font-semibold outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2",
                            selected ? "bg-primary text-primary shadow-xs ring-1 ring-secondary" : "text-quaternary hover:text-secondary",
                        )}
                    >
                        <OptionIcon className="size-4" aria-hidden="true" />
                        <span className="max-[360px]:sr-only">{id === "system" ? "سیستم" : label}</span>
                    </button>
                );
            })}
        </div>
    );
};
